import { DISPLAY_CACHE, THUMBNAIL_CACHE } from "./cacheNames"
import { STORES, idbClear } from "./db"
import {
  IMAGE_CORS,
  IMAGE_FETCH,
  fetchCorsImage,
  readImageCors,
  resetImageCors,
  writeImageCors
} from "./imageCors"

const CONCURRENCY = 4

export const IMAGE_CACHE_STATE = {
  IDLE: "idle",
  SAVING: "saving",
  DONE: "done",
  BLOCKED: "blocked",
  OFFLINE: "offline",
  UNSUPPORTED: "unsupported"
}

function hasCacheStorage() {
  return typeof caches !== "undefined"
}

// Downloads every thumbnail the menu references that is not saved yet.
export async function precacheThumbnails(urls, { onProgress, shouldStop }) {
  const total = urls.length
  if (!hasCacheStorage()) {
    onProgress({ saved: 0, total, state: IMAGE_CACHE_STATE.UNSUPPORTED })
    return
  }

  let saved = 0
  try {
    const cache = await caches.open(THUMBNAIL_CACHE)
    const cached = new Set((await cache.keys()).map((request) => request.url))
    const pending = urls.filter((url) => !cached.has(url))
    saved = total - pending.length

    if ((await readImageCors()) === IMAGE_CORS.BLOCKED) {
      onProgress({ saved, total, state: IMAGE_CACHE_STATE.BLOCKED })
      return
    }
    if (!pending.length) {
      onProgress({ saved, total, state: IMAGE_CACHE_STATE.DONE })
      return
    }

    onProgress({ saved, total, state: IMAGE_CACHE_STATE.SAVING })
    let stopState = null
    let next = 0

    const worker = async () => {
      while (!stopState && next < pending.length) {
        if (shouldStop?.()) {
          stopState = IMAGE_CACHE_STATE.IDLE
          return
        }
        const url = pending[next]
        next += 1
        const { outcome, response } = await fetchCorsImage(url)
        if (outcome === IMAGE_FETCH.OK) {
          await cache.put(url, response)
          await writeImageCors(IMAGE_CORS.OK)
          saved += 1
          if (!shouldStop?.()) onProgress({ saved, total, state: IMAGE_CACHE_STATE.SAVING })
        } else if (outcome === IMAGE_FETCH.BLOCKED) {
          stopState = IMAGE_CACHE_STATE.BLOCKED
          await writeImageCors(IMAGE_CORS.BLOCKED)
        } else if (outcome === IMAGE_FETCH.OFFLINE) {
          stopState = IMAGE_CACHE_STATE.OFFLINE
        }
        // A missing or broken image is skipped; the next sync retries it.
      }
    }

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, pending.length) }, worker))
    if (!shouldStop?.()) onProgress({ saved, total, state: stopState ?? IMAGE_CACHE_STATE.DONE })
  } catch {
    // Quota or storage failure: the menu still works, photos just stay online-only.
    if (!shouldStop?.()) onProgress({ saved, total, state: IMAGE_CACHE_STATE.UNSUPPORTED })
  }
}

// How many photos this device is holding, across both caches.
export async function countCachedImages() {
  if (!hasCacheStorage()) return 0
  try {
    const caches_ = await Promise.all([caches.open(THUMBNAIL_CACHE), caches.open(DISPLAY_CACHE)])
    const keys = await Promise.all(caches_.map((cache) => cache.keys()))
    return keys.reduce((total, entries) => total + entries.length, 0)
  } catch {
    return 0
  }
}

// Drops thumbnails the current menu no longer references.
export async function pruneThumbnails(urls) {
  if (!hasCacheStorage()) return
  try {
    const keep = new Set(urls)
    const cache = await caches.open(THUMBNAIL_CACHE)
    const requests = await cache.keys()
    await Promise.all(
      requests.filter((request) => !keep.has(request.url)).map((request) => cache.delete(request))
    )
  } catch {
    // Pruning is housekeeping; a failure leaves extra images, never missing ones.
  }
}

export async function clearImageCaches() {
  await resetImageCors()
  await idbClear(STORES.IMAGE_LRU)
  if (!hasCacheStorage()) return
  try {
    await Promise.all([caches.delete(THUMBNAIL_CACHE), caches.delete(DISPLAY_CACHE)])
  } catch {
    // Nothing to clear when Cache Storage is unavailable.
  }
}
